import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod' 
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import Button from '@/components/Button'
import { AxiosError } from 'axios'
import { isAxiosError } from '@/api/business' 
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchInformSequenceDocument } from '@/api/business/cockpit'
import { DetailNFKeys } from '@/queries/useDetailNFQuery'
import { Cockpit } from '@/queries/useCockpitQuery'

interface PropsSequenceDocument {
    document: string | null
    serie: string | null
    nf: string
}

const sequenceDocumentSchema = z.object({
    serie: z.string().min(1, 'Campo obrigatório'),
    document: z.string().min(1, 'Campo obrigatório'),
})
type SequenceDocumentSchemaType = z.infer<typeof sequenceDocumentSchema>

export function FormSequenceDocument( {document, serie, nf } : PropsSequenceDocument) { 
    const queryClient = useQueryClient()
    const { toast } = useToast()

    const form = useForm<SequenceDocumentSchemaType>({
        resolver: zodResolver(sequenceDocumentSchema),
        defaultValues: {
            document: document || '',
            serie: serie || '',
        },
    })

    const { mutate: onInformSequenceDocument, isPending } = useMutation({
        mutationFn: fetchInformSequenceDocument,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: DetailNFKeys.detail(nf) })
            queryClient.invalidateQueries({ queryKey: Cockpit.all })
            console.log('success',data)

            toast({
                title: data.body,
                variant: 'success',
            })
        },
        onError: (err: AxiosError) => {
            if (isAxiosError(err)) {
                toast({
                    title: 'Erro ao informar o documento de sequencia',
                    description: 'Tente novamente caso erro persistir entre em contato com o desenvolvimento,',
                    variant: 'destructive',
                })
            }
        },
    })

    const onSubmit = async (data: SequenceDocumentSchemaType) => {
        onInformSequenceDocument({
            SK_NF: nf, 
            DD_DOC_SEQUENCIA: data.document, 
            DD_SERIE_SEQUENCIA: data.serie
        })
    }
  
    return ( 
        <Form {...form}>
            <form
                className="grid grid-cols-1 md:grid-cols-1 gap-4"
                onSubmit={form.handleSubmit(onSubmit)}
            > 
                <FormField
                    name="document"
                    control={form.control}
                    render={({ field }) => {
                        return (
                            <FormItem>
                                <FormLabel className='text-sm font-medium text-muted-foreground'>Documento</FormLabel>
                                <FormControl>
                                    <Input {...field} className='w-full border-2 border-gray-500 text-sm rounded-md px-4 py-2 text-gray-50 outline-none focus:border-primary-500' />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )
                    }}
                /> 
                <FormField
                    name="serie"
                    control={form.control}
                    render={({ field }) => {
                        return (
                            <FormItem>
                                <FormLabel className='text-sm font-medium text-muted-foreground'>Nº Serie</FormLabel>
                                <FormControl>
                                    <Input {...field} className='w-full border-2 border-gray-500 text-sm rounded-md px-4 py-2 text-gray-50 outline-none focus:border-primary-500' />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )
                    }}
                /> 
                <div className='flex items-end'>
                    <Button className="w-full md:py-3 bg-black hover:bg-gray-900" isLoading={isPending}>
                        Confirmar
                    </Button> 
                </div>
            </form>
        </Form>
    
    );
}